import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import PlusIcon from '../assets/Icons/plus.svg';
import EditIcon from '../assets/Icons/reload.svg';
import TrashIcon from '../assets/Icons/black_X.svg';
import CloseIcon from '../assets/Icons/black_X.svg';
import ImportIcon from '../assets/Icons/plus.svg';

// Keep in sync with client/src/tenants/sharedComponents/gister/gisterComponentRegistry.jsx
const GISTER_COMPONENT_OPTIONS = [
  { key: 'personalInformation', label: "Patient's Personal Information" },
  { key: 'doctorMedicalConcentrate', label: 'Select doctor and type of laxative solution' },
  { key: 'procedureDetails', label: 'Procedure Details' },
  { key: 'medicalConcentrate', label: 'Medical Concentrate Selection' },
  { key: 'tavrProcedureSection', label: "Patient's Procedure (TAVR)" },
  { key: 'tavrMedicationSection', label: "Patient's Medications (TAVR)" },
];

export default function AdminPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [hospitals, setHospitals] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingHospital, setEditingHospital] = useState(null);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [editingStaff, setEditingStaff] = useState(null);
  const [modalOpen, setModalOpen] = useState(null); // 'hospital', 'department', 'staff'
  const [expandedSection, setExpandedSection] = useState('overview');
  const [duplicates, setDuplicates] = useState(null);
  const [duplicatesLoading, setDuplicatesLoading] = useState(false);
  const [activeView, setActiveView] = useState('facilities'); // 'facilities' | 'duplicates'
  const [brandingUrl, setBrandingUrl] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [fetchingBranding, setFetchingBranding] = useState(false);
  const [brandingError, setBrandingError] = useState('');

  useEffect(() => {
    fetchHospitals();
    fetchStatistics();
  }, []);

  const fetchHospitals = async () => {
    try {
      console.log('Fetching hospitals...');
      const { data } = await axios.get('/api/admin/hospitals');
      console.log('Response:', data);
      if (data.success) {
        console.log('Hospitals received:', data.data);
        setHospitals(data.data);
      } else {
        console.error('API returned success=false:', data);
      }
    } catch (error) {
      console.error('Failed to fetch hospitals:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        console.error('Error status:', error.response.status);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const { data } = await axios.get('/api/admin/statistics');
      if (data.success) {
        setStatistics(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  };

  const fetchDuplicates = async () => {
    setDuplicatesLoading(true);
    try {
      const { data } = await axios.get('/api/admin/duplicates');
      if (data.success) {
        setDuplicates(data.data);
        setActiveView('duplicates');
      }
    } catch (error) {
      console.error('Failed to fetch duplicates:', error);
    } finally {
      setDuplicatesLoading(false);
    }
  };

  const handleCreateHospital = () => {
    setEditingHospital({
      name: '',
      color1: '#84a4fc',
      color2: '#4A90E2',
      color3: '#f02a4c',
      gisterConfig: ['personalInformation', 'doctorMedicalConcentrate', 'procedureDetails'],
    });
    setModalOpen('hospital');
  };

  const toggleGisterComponent = (key) => {
    setEditingHospital(prev => {
      const current = prev?.gisterConfig || [];
      const isSelected = current.includes(key);
      return {
        ...prev,
        gisterConfig: isSelected ? current.filter(k => k !== key) : [...current, key],
      };
    });
  };

  const moveGisterComponent = (key, direction) => {
    setEditingHospital(prev => {
      const current = [...(prev?.gisterConfig || [])];
      const index = current.indexOf(key);
      const newIndex = index + direction;
      if (index === -1 || newIndex < 0 || newIndex >= current.length) return prev;
      [current[index], current[newIndex]] = [current[newIndex], current[index]];
      return { ...prev, gisterConfig: current };
    });
  };

  const handleEditHospital = (hospital) => {
    navigate(`/admin/facility/${hospital.id}`);
  };

  const handleDeleteHospital = async (id, name, departmentsCount, staffCount) => {
    if (!confirm(`Are you sure you want to delete "${name}"?\n\nThis will also delete:\n- ${departmentsCount} departments\n- ${staffCount} staff members\n\nThis action cannot be undone.`)) return;
    try {
      await axios.delete(`/api/admin/hospitals/${id}`);
      fetchHospitals();
    } catch (error) {
      console.error('Failed to delete facility:', error);
    }
  };

  const handleSaveHospital = async () => {
    try {
      if (editingHospital.id) {
        await axios.put(`/api/admin/hospitals/${editingHospital.id}`, editingHospital);
        setModalOpen(null);
        setEditingHospital(null);
        fetchHospitals();
      } else {
        const { data } = await axios.post('/api/admin/hospitals', editingHospital);
        setModalOpen(null);
        setEditingHospital(null);
        fetchHospitals();
        if (data.success && data.data?.id) {
          navigate(`/admin/facility/${data.data.id}`);
        }
      }
    } catch (error) {
      console.error('Failed to save hospital:', error);
    }
  };

  const handleLogoFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBrandingError('');
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      formData.append('name', editingHospital?.name || '');
      const { data } = await axios.post('/api/admin/hospitals/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data.success) {
        setEditingHospital(prev => ({ ...prev, logo: data.data.logo }));
      } else {
        setBrandingError('Failed to upload logo');
      }
    } catch (error) {
      console.error('Failed to upload logo:', error);
      setBrandingError('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  const handleFetchBranding = async () => {
    if (!brandingUrl) return;
    setBrandingError('');
    setFetchingBranding(true);
    try {
      const { data } = await axios.post('/api/admin/hospitals/fetch-branding', {
        url: brandingUrl,
        name: editingHospital?.name || '',
      });
      if (data.success) {
        setEditingHospital(prev => ({
          ...prev,
          logo: data.data.logo || prev.logo,
          color1: data.data.color1 || prev.color1,
          color2: data.data.color2 || prev.color2,
          color3: data.data.color3 || prev.color3,
        }));
      } else {
        setBrandingError(data.details || data.error || 'Failed to fetch branding from website');
      }
    } catch (error) {
      console.error('Failed to fetch branding:', error);
      const details = error.response?.data?.details || error.response?.data?.error;
      setBrandingError(details || 'Failed to fetch branding from website');
    } finally {
      setFetchingBranding(false);
    }
  };

  const handleCreateDepartment = (hospitalId) => {
    setEditingDepartment({ hospitalId, name: '' });
    setModalOpen('department');
  };

  const handleEditDepartment = (department) => {
    setEditingDepartment({ ...department });
    setModalOpen('department');
  };

  const handleDeleteDepartment = async (id) => {
    if (!confirm('Are you sure you want to delete this department?')) return;
    try {
      await axios.delete(`/api/admin/departments/${id}`);
      fetchHospitals();
    } catch (error) {
      console.error('Failed to delete department:', error);
    }
  };

  const handleSaveDepartment = async () => {
    try {
      if (editingDepartment.id) {
        await axios.put(`/api/admin/departments/${editingDepartment.id}`, editingDepartment);
      } else {
        await axios.post(`/api/admin/hospitals/${editingDepartment.hospitalId}/departments`, editingDepartment);
      }
      setModalOpen(null);
      setEditingDepartment(null);
      fetchHospitals();
    } catch (error) {
      console.error('Failed to save department:', error);
    }
  };

  const handleCreateStaff = (hospitalId, departmentId) => {
    setEditingStaff({ hospitalId, departmentId, email: '', name: '', phoneNumber: '', password: '', role: 'coordinator' });
    setModalOpen('staff');
  };

  const handleEditStaff = (staff) => {
    setEditingStaff({ ...staff, password: '' });
    setModalOpen('staff');
  };

  const handleDeleteStaff = async (id) => {
    if (!confirm('Are you sure you want to delete this staff member?')) return;
    try {
      await axios.delete(`/api/admin/staff/${id}`);
      fetchHospitals();
    } catch (error) {
      console.error('Failed to delete staff:', error);
    }
  };

  const handleSaveStaff = async () => {
    try {
      if (editingStaff.id) {
        await axios.put(`/api/admin/staff/${editingStaff.id}`, editingStaff);
      } else {
        await axios.post('/api/admin/staff', editingStaff);
      }
      setModalOpen(null);
      setEditingStaff(null);
      fetchHospitals();
    } catch (error) {
      console.error('Failed to save staff:', error);
    }
  };

  if (loading) return <Loading>Loading...</Loading>;

  const totalHospitals = hospitals.length;
  const totalDepartments = hospitals.reduce((sum, h) => sum + (h.departmentsCount || 0), 0);
  const totalStaff = hospitals.reduce((sum, h) => sum + (h.staffCount || 0), 0);

  return (
    <Container>
      <MainContent>
      <Header>
        <h1>Admin Dashboard</h1>
        <ButtonGroup>
          <AddButton onClick={handleCreateHospital}>
            <img src={PlusIcon} alt="" />
            Add Facility
          </AddButton>
          <ImportButton onClick={() => navigate('/admin/import')}>
            <img src={ImportIcon} alt="" />
            Import Patients
          </ImportButton>
        </ButtonGroup>
      </Header>

      {activeView === 'duplicates' && duplicates ? (
        <TableContainer>
          <TableHeader>
            <h2>Duplicate Cases ({duplicates.length} groups)</h2>
          </TableHeader>
          {duplicates.length === 0 ? (
            <EmptyState>No duplicate cases found.</EmptyState>
          ) : (
            <DuplicatesContent>
              {duplicates.map((group, idx) => (
                <DuplicateGroup key={idx}>
                  <DuplicateGroupHeader>
                    <span>Case # <strong>{group.zehutNumber}</strong></span>
                    <DuplicateBadge>{group.caseCount} cases</DuplicateBadge>
                  </DuplicateGroupHeader>
                  <DuplicateTable>
                    <thead>
                      <tr>
                        <DupTh>Case ID</DupTh>
                        <DupTh>Type</DupTh>
                        <DupTh>Created</DupTh>
                      </tr>
                    </thead>
                    <tbody>
                      {group.cases.map((c, cIdx) => (
                        <tr key={cIdx}>
                          <DupTd><code>{c.caseId?.slice(0, 8)}...</code></DupTd>
                          <DupTd>{c.type || 'N/A'}</DupTd>
                          <DupTd>{c.createdAt ? new Date(c.createdAt).toLocaleDateString() : 'N/A'}</DupTd>
                        </tr>
                      ))}
                    </tbody>
                  </DuplicateTable>
                </DuplicateGroup>
              ))}
            </DuplicatesContent>
          )}
        </TableContainer>
      ) : (
      <TableContainer>
        <TableHeader>
          <h2>Facilities ({hospitals.length})</h2>
        </TableHeader>
            <Table>
              <thead>
                <tr>
                  <Th>Facility</Th>
                  <Th>Departments</Th>
                  <Th>Staff</Th>
                  <Th align="right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {hospitals.map(hospital => (
                  <Tr key={hospital.id} onClick={() => handleEditHospital(hospital)}>
                    <Td>
                      <FacilityCell>
                        <FacilityLogoSmall $logo={hospital.logo}>
                          {hospital.logo
                            ? <img src={hospital.logo} alt="" onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }} />
                            : null}
                          <span style={{ display: hospital.logo ? 'none' : 'block' }}>{hospital.name?.[0]}</span>
                        </FacilityLogoSmall>
                        <span>{hospital.name}</span>
                      </FacilityCell>
                    </Td>
                    <Td>{hospital.departmentsCount || 0}</Td>
                    <Td>{hospital.staffCount || 0}</Td>
                    <Td align="right">
                      <ActionGroup onClick={e => e.stopPropagation()}>
                        <TableButton onClick={() => handleEditHospital(hospital)}>
                          <img src={EditIcon} alt="Edit" />
                        </TableButton>
                        <TableButton danger onClick={() => handleDeleteHospital(hospital.id, hospital.name, hospital.departmentsCount, hospital.staffCount)}>
                          <img src={TrashIcon} alt="Delete" />
                        </TableButton>
                      </ActionGroup>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            {hospitals.length === 0 && (
              <EmptyState>No facilities found. Add your first facility to get started.</EmptyState>
            )}
          </TableContainer>
          )}

      {/* Hospital Modal */}
      {modalOpen === 'hospital' && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <h2>{editingHospital?.id ? 'Edit Facility' : 'Add Facility'}</h2>
              <CloseButton onClick={() => setModalOpen(null)}>
                <img src={CloseIcon} alt="" />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormField>
                <label>Name</label>
                <Input
                  value={editingHospital?.name || ''}
                  onChange={e => setEditingHospital({ ...editingHospital, name: e.target.value })}
                  placeholder="Facility name"
                />
              </FormField>

              {editingHospital && (
                <>
                  <FormField>
                    <label>Logo</label>
                    <LogoUploadRow>
                      <LogoPreview>
                        {editingHospital?.logo
                          ? <img src={editingHospital.logo} alt="" />
                          : <span>{editingHospital?.name?.[0] || '?'}</span>}
                      </LogoPreview>
                      <UploadButtonLabel>
                        {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          disabled={uploadingLogo || !editingHospital?.name}
                          onChange={handleLogoFileChange}
                        />
                      </UploadButtonLabel>
                    </LogoUploadRow>
                    {!editingHospital?.name && (
                      <HintText>Enter a name above first (used to store the logo).</HintText>
                    )}
                    {brandingError && <ErrorText>{brandingError}</ErrorText>}
                  </FormField>

                  <FormField>
                    <label>Auto-fill from company website</label>
                    <BrandingRow>
                      <Input
                        value={brandingUrl}
                        onChange={e => setBrandingUrl(e.target.value)}
                        placeholder="https://company-website.com"
                      />
                      <FetchButton
                        type="button"
                        disabled={!brandingUrl || !editingHospital?.name || fetchingBranding}
                        onClick={handleFetchBranding}
                      >
                        {fetchingBranding ? 'Fetching...' : 'Fetch'}
                      </FetchButton>
                    </BrandingRow>
                    {!editingHospital?.name && (
                      <HintText>Enter a name above first (used to store the logo).</HintText>
                    )}
                  </FormField>

                  <ColorPicker>
                    <ColorField>
                      <label>Primary Color</label>
                      <ColorInput
                        type="color"
                        value={editingHospital?.color1 || '#84a4fc'}
                        onChange={e => setEditingHospital({ ...editingHospital, color1: e.target.value })}
                      />
                    </ColorField>
                    <ColorField>
                      <label>Secondary Color</label>
                      <ColorInput
                        type="color"
                        value={editingHospital?.color2 || '#4A90E2'}
                        onChange={e => setEditingHospital({ ...editingHospital, color2: e.target.value })}
                      />
                    </ColorField>
                    <ColorField>
                      <label>Accent Color</label>
                      <ColorInput
                        type="color"
                        value={editingHospital?.color3 || '#f02a4c'}
                        onChange={e => setEditingHospital({ ...editingHospital, color3: e.target.value })}
                      />
                    </ColorField>
                  </ColorPicker>

                  <FormField>
                    <label>Gister Components (staff intake form)</label>
                    <GisterComponentList>
                      {GISTER_COMPONENT_OPTIONS.map(option => {
                        const selected = editingHospital?.gisterConfig || [];
                        const isChecked = selected.includes(option.key);
                        const order = selected.indexOf(option.key);
                        return (
                          <GisterComponentRow key={option.key}>
                            <label>
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => toggleGisterComponent(option.key)}
                              />
                              {option.label}
                            </label>
                            {isChecked && (
                              <ReorderButtons>
                                <button type="button" disabled={order === 0} onClick={() => moveGisterComponent(option.key, -1)}>↑</button>
                                <button type="button" disabled={order === selected.length - 1} onClick={() => moveGisterComponent(option.key, 1)}>↓</button>
                              </ReorderButtons>
                            )}
                          </GisterComponentRow>
                        );
                      })}
                    </GisterComponentList>
                  </FormField>

                  <FormField>
                    <label>Language</label>
                    <Select
                      value={editingHospital?.language || 'en'}
                      onChange={e => setEditingHospital({ ...editingHospital, language: e.target.value })}
                    >
                      <option value="en">English</option>
                      <option value="he">Hebrew</option>
                    </Select>
                  </FormField>
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={() => setModalOpen(null)}>Cancel</CancelButton>
              <SaveButton onClick={handleSaveHospital}>Save</SaveButton>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* Department Modal */}
      {modalOpen === 'department' && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <h2>{editingDepartment?.id ? 'Edit Department' : 'Add Department'}</h2>
              <CloseButton onClick={() => setModalOpen(null)}>
                <img src={CloseIcon} alt="" />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormField>
                <label>Name</label>
                <Input
                  value={editingDepartment?.name || ''}
                  onChange={e => setEditingDepartment({ ...editingDepartment, name: e.target.value })}
                  placeholder="Department name"
                />
              </FormField>
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={() => setModalOpen(null)}>Cancel</CancelButton>
              <SaveButton onClick={handleSaveDepartment}>Save</SaveButton>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* Staff Modal */}
      {modalOpen === 'staff' && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <h2>{editingStaff?.id ? 'Edit Staff Member' : 'Add Staff Member'}</h2>
              <CloseButton onClick={() => setModalOpen(null)}>
                <img src={CloseIcon} alt="" />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormField>
                <label>Name</label>
                <Input
                  value={editingStaff?.name || ''}
                  onChange={e => setEditingStaff({ ...editingStaff, name: e.target.value })}
                  placeholder="Full name"
                />
              </FormField>
              <FormField>
                <label>Email</label>
                <Input
                  type="email"
                  value={editingStaff?.email || ''}
                  onChange={e => setEditingStaff({ ...editingStaff, email: e.target.value })}
                  placeholder="email@example.com"
                />
              </FormField>
              <FormField>
                <label>Phone Number</label>
                <Input
                  type="tel"
                  value={editingStaff?.phoneNumber || ''}
                  onChange={e => setEditingStaff({ ...editingStaff, phoneNumber: e.target.value })}
                  placeholder="+1 555 123 4567"
                />
              </FormField>
              <FormField>
                <label>Password {editingStaff?.id ? '(leave blank to keep current)' : ''}</label>
                <Input
                  type="password"
                  value={editingStaff?.password || ''}
                  onChange={e => setEditingStaff({ ...editingStaff, password: e.target.value })}
                  placeholder="Password"
                />
              </FormField>
              <FormField>
                <label>Role</label>
                <Select
                  value={editingStaff?.role || 'coordinator'}
                  onChange={e => setEditingStaff({ ...editingStaff, role: e.target.value })}
                >
                  <option value="coordinator">Coordinator</option>
                  <option value="admin">Admin</option>
                  <option value="Cardiology Coordinator">Cardiology Coordinator</option>
                </Select>
              </FormField>
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={() => setModalOpen(null)}>Cancel</CancelButton>
              <SaveButton onClick={handleSaveStaff}>Save</SaveButton>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
      </MainContent>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  min-height: 100vh;
  background: #f5f5f5;
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;

  h1 {
    margin: 0;
    font-size: 1.8rem;
    color: #333;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #f02a4c;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  img {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background: #d4223f;
  }
`;

const ImportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #84a4fc;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  img {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background: #4A90E2;
  }
`;

const HospitalsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
`;

const HospitalCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border-top: 4px solid ${props => props.$colors?.color1 || '#84a4fc'};
`;

const FacilityCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transition: all 0.2s;
  border: 2px solid transparent;

  &:hover {
    box-shadow: 0 4px 16px rgba(0,0,0,0.15);
    border-color: #f02a4c;
    transform: translateY(-2px);
  }
`;

const FacilityLogo = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 16px;
  background: ${props => props.$logo ? 'transparent' : 'linear-gradient(135deg, #84a4fc, #4A90E2)'};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const FacilityInitials = styled.span`
  color: white;
  font-size: 2rem;
  font-weight: 700;
`;

const FacilityName = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  text-align: center;
`;

const HospitalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  border-bottom: 1px solid #e5e5e5;
`;

const HospitalLogo = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: ${props => props.$logo ? 'transparent' : 'linear-gradient(135deg, #84a4fc, #4A90E2)'};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const HospitalInitials = styled.span`
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
`;

const HospitalInfo = styled.div`
  flex: 1;
`;

const HospitalName = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.25rem;
`;

const HospitalStats = styled.div`
  font-size: 0.85rem;
  color: #666;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const HospitalActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  
  img {
    width: 16px;
    height: 16px;
  }
  
  &:hover {
    background: #f5f5f5;
    border-color: #ccc;
  }
`;

const DepartmentsSection = styled.div`
  padding: 1.25rem;
`;

const DepartmentsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  
  h3 {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 600;
    color: #666;
  }
`;

const SmallAddButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid #e5e5e5;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  
  img {
    width: 14px;
    height: 14px;
  }
  
  &:hover {
    background: #f5f5f5;
  }
`;

const DepartmentCard = styled.div`
  background: #f9f9f9;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 0.75rem;
  border-left: 3px solid #84a4fc;
`;

const DepartmentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const DepartmentName = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: #333;
`;

const DepartmentActions = styled.div`
  display: flex;
  gap: 0.25rem;
`;

const SmallActionButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid #e5e5e5;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  
  img {
    width: 12px;
    height: 12px;
  }
  
  &:hover {
    background: #f5f5f5;
  }
`;

const StaffList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const StaffItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: white;
  border-radius: 6px;
`;

const StaffInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
`;

const StaffName = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: #333;
`;

const StaffEmail = styled.div`
  font-size: 0.75rem;
  color: #666;
`;

const StaffRole = styled.div`
  font-size: 0.7rem;
  color: #84a4fc;
  font-weight: 500;
`;

const StaffActions = styled.div`
  display: flex;
  gap: 0.25rem;
`;

const AddStaffButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: transparent;
  border: 1px dashed #84a4fc;
  border-radius: 6px;
  color: #84a4fc;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  margin-top: 0.5rem;
  
  img {
    width: 12px;
    height: 12px;
  }
  
  &:hover {
    background: rgba(132, 164, 252, 0.1);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: #999;
  font-size: 0.9rem;
`;

const Loading = styled.div`
  text-align: center;
  padding: 4rem;
  color: #666;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem;
  border-bottom: 1px solid #e5e5e5;
  
  h2 {
    margin: 0;
    font-size: 1.2rem;
    color: #333;
  }
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  
  img {
    width: 16px;
    height: 16px;
  }
  
  &:hover {
    background: #e5e5e5;
  }
`;

const ModalBody = styled.div`
  padding: 1.25rem;
`;

const FormField = styled.div`
  margin-bottom: 1rem;
  
  label {
    display: block;
    font-size: 0.85rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 0.5rem;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: #84a4fc;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: #84a4fc;
  }
`;

const BrandingRow = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const FetchButton = styled.button`
  padding: 0.75rem 1.25rem;
  background: #84a4fc;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s;

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: #4A90E2;
  }
`;

const LogoUploadRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const LogoPreview = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: #f5f5f5;
  border: 1px solid #e5e5e5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;

  span {
    font-size: 1.3rem;
    font-weight: 700;
    color: #999;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const UploadButtonLabel = styled.label`
  display: inline-flex;
  align-items: center;
  padding: 0.65rem 1.25rem;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #333;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
    border-color: #ccc;
  }
`;

const HintText = styled.div`
  font-size: 0.75rem;
  color: #999;
  margin-top: 0.4rem;
`;

const ErrorText = styled.div`
  font-size: 0.75rem;
  color: #d4223f;
  margin-top: 0.4rem;
`;

const ColorPicker = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;
`;

const GisterComponentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 0.75rem;
`;

const GisterComponentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.85rem;

  label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
  }
`;

const ReorderButtons = styled.div`
  display: flex;
  gap: 0.25rem;

  button {
    width: 22px;
    height: 22px;
    border: 1px solid #e5e5e5;
    background: white;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.75rem;
    line-height: 1;

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    &:hover:not(:disabled) {
      background: #f5f5f5;
    }
  }
`;

const ColorField = styled.div`
  label {
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 0.5rem;
  }
`;

const ColorInput = styled.input`
  width: 100%;
  height: 40px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  cursor: pointer;
  
  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }
  
  &::-webkit-color-swatch {
    border: none;
    border-radius: 6px;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.25rem;
  border-top: 1px solid #e5e5e5;
`;

const CancelButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: 1px solid #e5e5e5;
  background: white;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  
  &:hover {
    background: #f5f5f5;
  }
`;

const SaveButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: #f02a4c;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  
  &:hover {
    background: #d4223f;
  }
`;

const StatCard = styled.div`
  background: ${props => props.large ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.15)'};
  border-radius: 8px;
  padding: ${props => props.large ? '1.25rem' : '1rem'};
  margin-bottom: 0.75rem;
  border: ${props => props.large ? '2px solid rgba(255,255,255,0.3)' : 'none'};
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div`
  font-size: ${props => props.large ? '2rem' : '1.5rem'};
  font-weight: 700;
  color: white;
`;

const QuickAction = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.75rem;
  background: white;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #f02a4c;
  cursor: pointer;
  transition: all 0.2s;
  
  img {
    width: 16px;
    height: 16px;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.9);
  }
`;

const HospitalList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const HospitalListItem = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 0.75rem;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const HospitalListItemName = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: white;
  margin-bottom: 0.25rem;
`;

const HospitalListItemCount = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
`;

// Table styles
const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow: hidden;
`;

const TableHeader = styled.div`
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e5e5;
  background: #f9f9f9;

  h2 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: #333;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
`;

const Th = styled.th`
  padding: 1rem 1.5rem;
  text-align: ${props => props.align || 'left'};
  font-weight: 600;
  color: #666;
  background: #f5f5f5;
  border-bottom: 1px solid #e5e5e5;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Tr = styled.tr`
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #f9f9f9;
  }

  &:not(:last-child) td {
    border-bottom: 1px solid #f0f0f0;
  }
`;

const Td = styled.td`
  padding: 1rem 1.5rem;
  text-align: ${props => props.align || 'left'};
  color: #333;
`;

const FacilityCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 500;
`;

const FacilityLogoSmall = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: ${props => props.$logo ? 'transparent' : 'linear-gradient(135deg, #84a4fc, #4A90E2)'};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  font-size: 0.9rem;
  font-weight: 600;
  color: white;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const TableButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid ${props => props.danger ? '#ffcdd2' : '#e5e5e5'};
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;

  img {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: ${props => props.danger ? '#ffebee' : '#f5f5f5'};
    border-color: ${props => props.danger ? '#ef9a9a' : '#ccc'};
  }
`;

// Duplicates view styles
const DuplicatesContent = styled.div`
  padding: 1rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const DuplicateGroup = styled.div`
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
`;

const DuplicateGroupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #f9f9f9;
  border-bottom: 1px solid #e5e5e5;
  font-size: 0.9rem;
`;

const DuplicateBadge = styled.span`
  background: #f02a4c;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
`;

const DuplicateTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
`;

const DupTh = styled.th`
  padding: 0.5rem 1rem;
  text-align: left;
  font-weight: 600;
  color: #666;
  font-size: 0.75rem;
  text-transform: uppercase;
  background: #fafafa;
`;

const DupTd = styled.td`
  padding: 0.5rem 1rem;
  border-top: 1px solid #f0f0f0;
  color: #333;

  code {
    background: #f0f0f0;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.8rem;
  }
`;
